// src/components/common/SiteFooter.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../../context/CookieConsentContext';
import '../../styles/SiteFooter.css';
import FollowUs from './FollowUs';

const LEGAL_LINKS = [
  { to: '/terms', label: 'Terms of Service' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/provider-agreement', label: 'Service Provider Agreement' },
  { to: '/cancellation-policy', label: 'Cancellation Policy' },
  { to: '/community-standards', label: 'Community Standards' },
];

const LEGALS_MENU_ID = 'site-footer-legals-menu';

/**
 * Single shared footer used across all public marketing and legal pages.
 * Centralizing this avoids the previously-duplicated (and in some cases
 * dead/non-functional) footer markup that existed per-page.
 */
const SiteFooter: React.FC = () => {
  const { openPreferences } = useCookieConsent();
  const [isLegalsOpen, setIsLegalsOpen] = useState(false);
  const legalsRef = useRef<HTMLDivElement>(null);
  const legalsTriggerRef = useRef<HTMLButtonElement>(null);

  const closeLegals = () => setIsLegalsOpen(false);
  const toggleLegals = () => setIsLegalsOpen((open) => !open);

  // Click-outside and Escape close the dropdown; consistent across desktop,
  // tablet, and mobile since this is triggered purely by click/tap, never hover.
  useEffect(() => {
    if (!isLegalsOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (legalsRef.current && !legalsRef.current.contains(event.target as Node)) {
        closeLegals();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLegals();
        legalsTriggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLegalsOpen]);

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <nav className="site-footer__links" aria-label="Footer navigation">
          <Link to="/about">About Us</Link>
          <Link to="/services">Services</Link>
          <Link to="/contact">Contact</Link>
          <div className="site-footer__legals" ref={legalsRef}>
            <button
              type="button"
              ref={legalsTriggerRef}
              className="site-footer__link-button site-footer__legals-trigger"
              onClick={toggleLegals}
              aria-haspopup="true"
              aria-expanded={isLegalsOpen}
              aria-controls={LEGALS_MENU_ID}
            >
              Legals
            </button>
            {isLegalsOpen && (
              <ul className="site-footer__legals-menu" id={LEGALS_MENU_ID} role="menu">
                {LEGAL_LINKS.map(({ to, label }) => (
                  <li key={to} role="none">
                    <Link to={to} role="menuitem" onClick={closeLegals}>
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="button" className="site-footer__link-button" onClick={openPreferences}>
            Cookie Settings
          </button>
        </nav>
        <FollowUs />
        <p className="site-footer__copyright">
          © {new Date().getFullYear()} LookBel. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

export default SiteFooter;
